<!--
/*
 * Компонент AdminGuides — управление инструкциями (PDF-гайдами)
 *
 * Основные функции:
 * - Отображение списка загруженных инструкций
 * - Скачивание PDF-файла инструкции через элемент a с атрибутом download
 * - Загрузка новой инструкции через диалоговое окно
 * - Обновление существующей инструкции (замена PDF)
 * - Удаление инструкции
 */
-->
<script setup lang="ts">
// Хуки жизненного цикла и реактивность Vue
import { ref, onMounted } from 'vue'
import { usePanZoom } from '@/composables/usePanZoom'
// Определение размера экрана — для динамического padding и адаптивного диалога
import { useDisplay } from 'vuetify'
// Утилита определения Telegram-контекста
import { isTelegram } from '@/utils/isTelegram'
// Стор администрирования — CRUD-операции с гайдами
import { useAdminStore } from '@/modules/admin/store/adminStore'
// Утилита Pinia для реактивных ссылок на стейт стора
import { storeToRefs } from 'pinia'
// Компонент заголовка секции
import SectionHeader from '@/components/SectionHeader.vue'
// Типизация гайда из сгенерированных proto-стабов
import type { Guide } from '@vpn-tg/vue-client-grpc/proto/guide/guide-endpoint'
import { formatGuideText } from '@/utils/formatGuideText'

// Инициализация стора администрирования
const adminStore = useAdminStore()
// Определение мобильного экрана для динамического padding и диалога
const { mobile, smAndDown } = useDisplay()
// Реактивная ссылка на список гайдов из стора
const { guides } = storeToRefs(adminStore)

// Флаг видимости диалогового окна создания новой инструкции
const isCreateDialogOpen = ref(false)
// Название новой инструкции для загрузки
const newGuideName = ref('')
// Текст новой инструкции
const newGuideText = ref('')
// Выбранный PDF-файл для загрузки новой инструкции
const newGuideFile = ref<File | null>(null)

// Состояние диалога редактирования гайда
const isEditDialogOpen = ref(false)
// Редактируемый гайд (для получения id)
const editingGuide = ref<Guide | null>(null)
// Название гайда в форме редактирования
const editGuideName = ref('')
// Текст гайда в форме редактирования
const editGuideText = ref('')
// Новый PDF-файл в форме редактирования (опционально)
const editGuideFile = ref<File | null>(null)

// Состояние для встроенного просмотра изображений
const isViewDialogOpen = ref(false)
const viewingImageUrl = ref('')
const viewingImageName = ref('')
const viewingGuideText = ref('')

// Использование composable для Pan & Zoom
const {
  zoomFactor,
  translateX,
  translateY,
  isTransitioning,
  viewerContainerRef,
  zoomIn,
  zoomOut,
  zoomReset
} = usePanZoom(isViewDialogOpen)

/**
 * Открывает изображение гайда во встроенном модальном окне
 *
 * @param {Guide} guide - Объект гайда с идентификатором
 */
const viewGuide = async (guide: Guide) => {
  const data = await adminStore.getGuide(guide.id)
  if (!data) return

  const blob = new Blob([data.pdfFile as BlobPart], { type: 'image/*' })
  viewingImageUrl.value = URL.createObjectURL(blob)
  viewingImageName.value = data.name || guide.name
  viewingGuideText.value = data.text || guide.text || ''
  isViewDialogOpen.value = true
}

/**
 * Закрывает просмотрщик и очищает Object URL
 */
const closeViewDialog = () => {
  isViewDialogOpen.value = false
  viewingGuideText.value = ''
  if (viewingImageUrl.value) {
    URL.revokeObjectURL(viewingImageUrl.value)
    viewingImageUrl.value = ''
  }
}

/**
 * Открывает диалог редактирования гайда
 *
 * @param {Guide} guide - Объект гайда для редактирования
 */
const openEditDialog = (guide: Guide) => {
  editingGuide.value = guide
  editGuideName.value = guide.name
  editGuideText.value = guide.text || ''
  editGuideFile.value = null
  isEditDialogOpen.value = true
}

/**
 * Сохраняет изменения гайда (PATCH-семантика)
 */
const saveEditGuide = async () => {
  if (!editingGuide.value) return

  const id = editingGuide.value.id
  const newName = editGuideName.value.trim() !== editingGuide.value.name
    ? editGuideName.value.trim()
    : undefined
  const currentText = editingGuide.value.text || ''
  const newText = editGuideText.value !== currentText
    ? editGuideText.value
    : undefined
  let newPdfFile: Uint8Array | undefined
  if (editGuideFile.value) {
    const buffer = await editGuideFile.value.arrayBuffer()
    newPdfFile = new Uint8Array(buffer)
  }

  if (newName === undefined && !newPdfFile && newText === undefined) {
    isEditDialogOpen.value = false
    return
  }

  const success = await adminStore.updateGuide(id, newName, newPdfFile, newText)
  if (success) {
    isEditDialogOpen.value = false
    editingGuide.value = null
  }
}

/**
 * Обработчик выбора файла в диалоге редактирования
 */
const onEditFileSelected = (files: File | File[]) => {
  editGuideFile.value = Array.isArray(files) ? files[0] || null : files || null
}

/**
 * Обработчик выбора файла в v-file-input диалога создания
 *
 * @param {File | File[]} files - Выбранный файл или массив файлов из Vuetify
 */
const onFileSelected = (files: File | File[]) => {
  newGuideFile.value = Array.isArray(files) ? files[0] || null : files || null
}

/**
 * Создаёт новую инструкцию с названием, текстом и файлом
 */
const createGuide = async () => {
  if (!newGuideName.value.trim() || !newGuideFile.value) return

  const buffer = await newGuideFile.value.arrayBuffer()
  const success = await adminStore.createGuide(
    newGuideName.value,
    new Uint8Array(buffer),
    newGuideText.value,
  )

  if (success) {
    isCreateDialogOpen.value = false
    newGuideName.value = ''
    newGuideText.value = ''
    newGuideFile.value = null
  }
}

// Загрузка списка гайдов при монтировании компонента
onMounted(async () => await adminStore.getGuides())
</script>

<template>
  <div class="d-flex flex-column ga-4 pt-4" :class="{ 'pa-4': isTelegram() || mobile }">
    <header>
      <section-header />
    </header>

    <v-btn
      block
      color="primary"
      variant="outlined"

      prepend-icon="ri-add-line"
      @click="isCreateDialogOpen = true"
    >
      Загрузить
    </v-btn>

    <div v-if="guides.length === 0" class="text-center text-text-disabled pa-4">
      Инструкции не загружены
    </div>

    <v-card v-for="guide in guides" :key="guide.id" variant="outlined" class="pa-3">
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex flex-column">
          <span class="text-body-1">{{ guide.name }}</span>
          <!-- TODO: дата загрузки — добавить когда появится в proto -->
        </div>
        <div class="d-flex ga-1">
          <v-tooltip text="Посмотреть" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon="ri-share-forward-line"
          
                variant="text"
                @click="viewGuide(guide)"
              />
            </template>
          </v-tooltip>
          <v-tooltip text="Обновить" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon="ri-upload-2-line"
          
                variant="text"
                @click="openEditDialog(guide)"
              />
            </template>
          </v-tooltip>
          <v-tooltip text="Удалить" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon="ri-delete-bin-line"
          
                variant="text"
                color="error"
                @click="adminStore.deleteGuide(guide.id)"
              />
            </template>
          </v-tooltip>
        </div>
      </div>
    </v-card>

    <v-dialog v-model="isCreateDialogOpen" max-width="500" :fullscreen="mobile">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div>Загрузить инструкцию</div>
          <v-btn icon="ri-close-line" variant="text" @click="isCreateDialogOpen = false" />
        </v-card-title>
        <v-card-text class="d-flex flex-column ga-3">
          <v-text-field
            v-model="newGuideName"
            label="Название"
            variant="outlined"
            hide-details
          />
          <v-textarea
            v-model="newGuideText"
            label="Текст инструкции"
            variant="outlined"
            rows="4"
            auto-grow
            hide-details
            hint="Можно вставлять ссылки (https://...)"
            persistent-hint
          />
          <v-file-input
            label="Изображение инструкции"
            variant="outlined"
            accept="image/*"
            hide-details
            @update:model-value="onFileSelected"
          />
          <div :class="['d-flex ga-2', mobile ? 'flex-column-reverse' : 'justify-end']">
            <v-btn variant="text" @click="isCreateDialogOpen = false">Отмена</v-btn>
            <v-btn color="primary" variant="outlined" @click="createGuide">Загрузить</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isEditDialogOpen" max-width="500" :fullscreen="mobile">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div>Редактировать инструкцию</div>
          <v-btn icon="ri-close-line" variant="text" @click="isEditDialogOpen = false" />
        </v-card-title>
        <v-card-text class="d-flex flex-column ga-3">
          <v-text-field
            v-model="editGuideName"
            label="Название"
            variant="outlined"
            hide-details
          />
          <v-textarea
            v-model="editGuideText"
            label="Текст инструкции"
            variant="outlined"
            rows="4"
            auto-grow
            hide-details
            hint="Можно вставлять ссылки (https://...)"
            persistent-hint
          />
          <v-file-input
            label="Новый файл изображения (опционально)"
            variant="outlined"
            accept="image/*"
            hide-details
            @update:model-value="onEditFileSelected"
          />
          <div :class="['d-flex ga-2', mobile ? 'flex-column-reverse' : 'justify-end']">
            <v-btn variant="text" @click="isEditDialogOpen = false">Отмена</v-btn>
            <v-btn color="primary" variant="outlined" @click="saveEditGuide">Сохранить</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Встроенный вьювер изображений -->
    <v-dialog
      :model-value="isViewDialogOpen"
      :fullscreen="smAndDown"
      :width="smAndDown ? undefined : 1000"
      @update:model-value="closeViewDialog"
    >
      <v-card class="d-flex flex-column h-100">
        <v-card-title class="d-flex align-center justify-space-between border-b pa-4">
          <div class="text-truncate font-weight-bold" style="max-width: 60%">
            Просмотр: {{ viewingImageName }}
          </div>
          <div class="d-flex align-center ga-1">
            <v-btn
              icon="ri-zoom-in-line"
              variant="text"
              density="comfortable"
              :disabled="zoomFactor >= 20"
              @click="zoomIn"
            />
            <v-btn
              icon="ri-zoom-out-line"
              variant="text"
              density="comfortable"
              :disabled="zoomFactor <= 0.5"
              @click="zoomOut"
            />
            <v-btn
              icon="ri-aspect-ratio-line"
              variant="text"
              density="comfortable"
              :disabled="zoomFactor === 1 && translateX === 0 && translateY === 0"
              @click="zoomReset"
            />
            <v-btn
              icon="ri-close-line"
              variant="text"
              density="comfortable"
              class="ml-2"
              @click="closeViewDialog"
            />
          </div>
        </v-card-title>
        <v-card-text
          class="pa-0 d-flex flex-column"
          style="background-color: #121212; min-height: 400px; height: calc(100vh - 80px); overflow: auto;"
        >
          <div
            v-if="viewingGuideText"
            class="guide-text pa-4"
          >
            <div class="d-flex align-center ga-3">
              <v-icon icon="ri-information-line" color="info" size="24" class="flex-shrink-0" />
              <div
                class="guide-text__body text-body-1"
                v-html="formatGuideText(viewingGuideText)"
              />
            </div>
          </div>
          <div
            ref="viewerContainerRef"
            style="display: flex; justify-content: center; align-items: center; width: 100%; min-height: 50%; flex: 1 1 auto; overflow: hidden; position: relative; touch-action: none; user-select: none;"
          >
            <img
              :src="viewingImageUrl"
              alt="Инструкция"
              :style="{
                transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${zoomFactor})`,
                transition: isTransitioning ? 'transform 0.2s ease-out' : 'none',
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                transformOrigin: 'center center',
                pointerEvents: 'none',
                userSelect: 'none'
              }"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.guide-text {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgba(33, 150, 243, 0.16);
  border-bottom: 1px solid rgba(33, 150, 243, 0.45);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  white-space: normal;
  word-break: break-word;
}
.guide-text__body {
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.95);
}
.guide-text :deep(a) {
  color: #90caf9;
  font-weight: 600;
  text-decoration: underline;
}
</style>
